import { today } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useMutation } from "@tanstack/react-query";

import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";

import { useAppForm } from "../../base/form";

export function CopyScheduleForm() {
  const trpc = useTRPC();
  const { mutate } = useMutation(
    trpc.tournaments.duplicateSchedule.mutationOptions(),
  );

  const form = useAppForm({
    defaultValues: {
      startDate: today(getDefaultTimeZone()).set({ month: 1, day: 1 }),
      endDate: today(getDefaultTimeZone()).set({ month: 12, day: 31 }),
      addDays: 364,
    },
    onSubmit: ({ value: { startDate, endDate, addDays } }) => {
      mutate({
        startDate: startDate.toString(),
        endDate: endDate.toString(),
        addDays,
      });
    },
  });

  const dateFormatter = useDateFormatter({
    dateStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();

        form.handleSubmit();
      }}
    >
      <form.Alert
        color="info"
        title="Info"
        description="You cannot undo this. However you can use the delete schedule form to delete all tournaments in the range of dates for tournaments created by this action."
      />

      <form.AppField
        name="startDate"
        children={(field) => (
          <field.DatePicker
            label="Start Date"
            className="col-span-2"
            field={field}
          />
        )}
      />

      <form.AppField
        name="endDate"
        children={(field) => (
          <field.DatePicker
            label="End Date"
            className="col-span-2"
            field={field}
          />
        )}
      />

      <form.AppField
        name="addDays"
        children={(field) => (
          <field.Number
            label="Jump Days"
            className="col-span-2"
            field={field}
            minValue={1}
          />
        )}
      />

      <form.AppForm>
        <form.Footer>
          <form.Subscribe selector={({ values }) => values}>
            {(values) => (
              <form.ConfirmSubmitButton
                requireChange={false}
                description={
                  <div className="space-y flex flex-col">
                    <div className="flex flex-row space-x-2">
                      <span className="font-semibold">Start date:</span>
                      <span>
                        {dateFormatter.format(
                          values.startDate.toDate(getDefaultTimeZone()),
                        )}
                      </span>
                    </div>
                    <div className="flex flex-row space-x-2">
                      <span className="font-semibold">End date:</span>
                      <span>
                        {dateFormatter.format(
                          values.endDate.toDate(getDefaultTimeZone()),
                        )}
                      </span>
                    </div>
                    <div className="flex flex-row space-x-2">
                      <span className="font-semibold">Jump days:</span>
                      <span>{values.addDays}</span>
                    </div>
                    <p className="mt-2">
                      If you want to undo this action, use the{" "}
                      <span className="font-semibold italic">
                        Delete Schedule
                      </span>{" "}
                      form to delete the generated range.
                    </p>
                  </div>
                }
              >
                Submit
              </form.ConfirmSubmitButton>
            )}
          </form.Subscribe>
        </form.Footer>
      </form.AppForm>
    </form>
  );
}
